// CROWN GRAB (§4.3): respawns, a single crown, first to the goal in crown time (else the most at
// the cap). The crown is a CROWN entity (`data` = holder, `data2` = the slot locked out of
// re-picking it, `timer` = that lock's ticks left) whose physics lives here, not in the generic
// entity step:
//  - it hovers 0.5 m over the map center (or the nearest open floor: the Teacup cup and the Spin
//    Cycle hub sit on the center), is picked up by a non-ghost body within 0.8 m (|Δz| ≤ 1.2),
//    and rides on its holder (0.9× speed, no items, shove and grab allowed);
//  - it pops only when another bean knocks it off (INT-A design direction, logged in
//    docs/decisions/INTEGRATION.md): a bean-caused hit (shove, throw, glove, bomb, peel, plunger,
//    pebble; a clash is a parry and keeps it), a stomp or body slam that sends the holder over the
//    edge, or a grab. Passive hazard impulses (bumpers, the ball, flippers, slingshots, sweepers,
//    flames, bubbles, the cup, crates) never pop it. It flies `popSpeed` along the hit (± a seeded
//    40°) with `popVz`; the previous holder can't re-pick it for 30 ticks;
//  - a holder who goes over the edge (FALLING: the KO to come) has it flung back toward the stage;
//    a crown that comes down over the void is lost and comes back at the spawn point
//    `voidRespawnTicks` later (CROWN_LOST, then CROWN_RESPAWN);
//  - Teams: crown time is summed per team, and only enemies knock it off.
// Server-authoritative except the crown's ballistic flight and its riding the holder, which also
// run in predict mode (§18.2); the holder's speed comes from the replicated `crownHolder`.
import { DT, NO_SLOT, TEAM_RESULT, TICK_HZ, TUNE, VOID } from '../constants';
import { EV, emit } from '../events';
import { BS, Cause, EK, ModeId, isHazardCause } from '../ids';
import type { RoundClock } from '../maps/clock';
import { MAPS } from '../maps/index';
import { SolidKind, type Solid } from '../maps/mapTypes';
import { DEG, approachVec, closestOnSegment, v2 } from '../math';
import { randRange } from '../rng';
import { findEntity, spawnEntity } from '../sim/entities';
import { isPredicting, isTeammate } from '../sim/world';
import type { Bean, Entity, Vec2, World } from '../types';
import { tickRespawns } from './common';
import type { ModeRules, RoundResult } from './modeTypes';
import { countActive } from './royale';
import { datan2, dcos, dsin } from '../sim/dmath';

const C = TUNE.crown;

/** Crown body physics (mode-local tuning, like a map's TUNING). */
export const CROWN_TUNING = {
  /** Collision radius against solids. */
  radius: 0.3,
  /** Height above the holder's feet while worn (renderers put it on the head). */
  wornZ: 1.15,
  /** Bounce off solids, and sliding friction while hovering (tilted saucer, blast pushes). */
  restitution: 0.5, friction: 6,
  /** Below this height over the void a falling crown is lost. */
  lostZ: -1,
  /** A respawned crown drops in from this height over its spawn point. */
  respawnDropZ: 4,
  /** Clearance kept between the spawn point and solids it can't pass (a bean must reach it). */
  spawnClearance: 0.55,
  spawnSearchStep: 0.1, spawnSearchMax: 80,
  /**
   * A lost crown drops back in where the map's danger stays below `respawnMaxDanger` over the
   * drop and the moments after it (`respawnSettleTicks`), searched on rings out to `respawnRingMax`.
   */
  respawnSettleTicks: 90, respawnMaxDanger: 0.5, respawnRingStep: 0.5, respawnRingMax: 10,
  /** With no ground anywhere to drop onto, the respawn is retried this many ticks later. */
  respawnRetryTicks: 15,
  /** A stage pop (no hit to follow) lands where the danger stays low this long after touchdown. */
  landSettleTicks: 30,
  /** Closer than this to its home, a stage pop goes out behind the holder instead. */
  stageMinDist: 0.5,
  /** A stage pop lands with floor all around it within this radius (it may drift a little). */
  landFooting: 0.6,
} as const;

/** Per-world server bookkeeping: KOs credited per slot (beans respawn, so `koBy` is not enough). */
const koCounts = new WeakMap<World, number[]>();

function kosOf(w: World): number[] {
  let k = koCounts.get(w);
  if (!k) {
    k = new Array<number>(w.beans.length).fill(0);
    koCounts.set(w, k);
  }
  return k;
}

const solidScratch: Solid[] = [];
const contact: Vec2 = v2();
const spawnPoint: Vec2 = v2();

/** True when the crown can rest at (x, y): there is ground, and no solid it can't pass is within reach of a bean. */
function crownSpotClear(w: World, x: number, y: number, n: number): boolean {
  const g = MAPS[w.cfg.mapId].groundAt(w, x, y);
  if (g === VOID) return false;
  for (let k = 0; k < n; k++) {
    const s = solidScratch[k];
    if (s.top - TUNE.collide.topSlack <= g + C.spawnZ) continue;
    const d = s.kind === SolidKind.CIRCLE ? Math.hypot(x - s.ax, y - s.ay) : Math.sqrt(distSq(x, y, s));
    if (d < s.r + CROWN_TUNING.spawnClearance) return false;
  }
  return true;
}

/** The crown's home: the map center, nudged toward the camera (+y) until a bean can reach it. */
export function crownSpawn(w: World, out: Vec2 = v2()): Vec2 {
  const map = MAPS[w.cfg.mapId];
  const n = map.solids(w, solidScratch);
  out.x = map.center.x;
  out.y = map.center.y;
  for (let i = 0; i < CROWN_TUNING.spawnSearchMax; i++) {
    if (crownSpotClear(w, out.x, out.y, n)) return out;
    out.y += CROWN_TUNING.spawnSearchStep;
  }
  out.x = map.center.x;
  out.y = map.center.y;
  return out;
}

/**
 * Where a lost crown drops back in: its home (`crownSpawn`) when that ground will still be there
 * and quiet once it lands, else the nearest such spot to the map center (rings 0.5 m apart): a
 * Crumble Cake center that has crumbled, a sweeper arm about to pass. Deterministic, no RNG.
 */
export function crownRespawnPoint(w: World, out: Vec2 = v2()): Vec2 {
  const map = MAPS[w.cfg.mapId];
  const ahead = CROWN_TUNING.respawnSettleTicks;
  const steady = (x: number, y: number): boolean => map.danger(w, x, y, ahead) < CROWN_TUNING.respawnMaxDanger;
  crownSpawn(w, out);
  if (map.groundAt(w, out.x, out.y) !== VOID && steady(out.x, out.y)) return out;
  const n = map.solids(w, solidScratch);
  const c = map.center;
  for (let r = CROWN_TUNING.respawnRingStep; r <= CROWN_TUNING.respawnRingMax; r += CROWN_TUNING.respawnRingStep) {
    const k = Math.max(8, Math.round(r * 8));
    for (let i = 0; i < k; i++) {
      // Start from +y (toward the camera), like `crownSpawn`.
      const a = Math.PI / 2 + (i / k) * Math.PI * 2;
      const x = c.x + dcos(a) * r;
      const y = c.y + dsin(a) * r;
      if (crownSpotClear(w, x, y, n) && steady(x, y)) {
        out.x = x;
        out.y = y;
        return out;
      }
    }
  }
  return out;
}

function distSq(x: number, y: number, s: Solid): number {
  closestOnSegment(x, y, s.ax, s.ay, s.bx, s.by, contact);
  return (x - contact.x) ** 2 + (y - contact.y) ** 2;
}

/** The crown entity, if it exists. */
export function crownEntity(w: World): Entity | undefined {
  return w.mode.crownEntId > 0 ? findEntity(w, w.mode.crownEntId) : undefined;
}

/** Bodies that can pick the crown up: in play, on their feet or in the air, not hanging or held. */
function canPick(b: Bean): boolean {
  if (!b.active || b.out) return false;
  switch (b.state) {
    case BS.TUMBLE: case BS.TEETER: case BS.LEDGE: case BS.CLIMB: case BS.GRABBED: case BS.FALLING:
    case BS.GHOST: case BS.RESPAWN: case BS.FROZEN: case BS.SPUN:
      return false;
    default:
      return true;
  }
}

/**
 * Impulse causes that knock the crown off when an enemy bean deals them. Hazards (§2.11's
 * HAZARD_CAUSES), clashes (a parry), the potato and causeless impulses never do.
 */
export function crownPopCause(cause: Cause): boolean {
  if (isHazardCause(cause)) return false;
  switch (cause) {
    case Cause.SHOVE: case Cause.THROW: case Cause.GLOVE: case Cause.BOMB: case Cause.PEEL:
    case Cause.PLUNGER: case Cause.STOMP: case Cause.PEBBLE: case Cause.BODY:
      return true;
    default:
      return false;
  }
}

/** True when `by` is a bean other than the holder and not on its team (Teams: enemies only). */
function enemyOf(w: World, by: number, holder: number): boolean {
  return by !== NO_SLOT && by !== holder && by < w.beans.length && !isTeammate(w, by, holder);
}

/** Why the holder's state knocks the crown off (`popReason`). */
const Pop = { NONE: 0, FALL: 1, KNOCK: 2 } as const;
type Pop = (typeof Pop)[keyof typeof Pop];

/**
 * The holder's state knocks the crown off: FALLING (the KO to come, whatever sent them over), a
 * grab by an enemy, or an edge or tumble state that an enemy bean's impulse caused within
 * `popTouchTicks` (a stomp, a body slam over the rim). A hazard's impulse after that touch
 * overwrites `touchCause` (§2.11 refresh), so a bumper or a sweeper never knocks it off by itself.
 */
function popReason(w: World, b: Bean): Pop {
  switch (b.state) {
    case BS.FALLING:
      return Pop.FALL;
    case BS.GRABBED:
      return enemyOf(w, b.partner, b.slot) ? Pop.KNOCK : Pop.NONE;
    case BS.TEETER: case BS.LEDGE: case BS.CLIMB: case BS.TUMBLE:
      return enemyOf(w, b.touchBy, b.slot) && crownPopCause(b.touchCause) && w.tick - b.touchTick <= C.popTouchTicks ? Pop.KNOCK : Pop.NONE;
    default:
      return Pop.NONE;
  }
}

/** Crown time of `slot`'s side: its own, or its team's sum in Teams. */
export function crownTimeOf(w: World, slot: number): number {
  const b = w.beans[slot];
  if (!w.cfg.teamsOn || b.team === 0) return b.crownTicks;
  let sum = 0;
  for (const o of w.beans) if (o.active && o.team === b.team) sum += o.crownTicks;
  return sum;
}

function pickUp(w: World, e: Entity, b: Bean): void {
  w.mode.crownHolder = b.slot;
  b.holdsCrown = true;
  e.data = b.slot;
  e.data2 = NO_SLOT;
  e.timer = 0;
  wear(e, b);
  emit(w, EV.CROWN_PICK, b.slot, NO_SLOT, 0, 0, b.pos.x, b.pos.y);
}

function wear(e: Entity, b: Bean): void {
  e.pos.x = b.pos.x;
  e.pos.y = b.pos.y;
  e.z = b.z + CROWN_TUNING.wornZ;
  e.vel.x = b.vel.x;
  e.vel.y = b.vel.y;
  e.vz = b.vz;
}

/** Pops the crown off its holder along (dx, dy), rotated by a seeded ±40° (server). */
function popCrown(w: World, dx: number, dy: number, spread = true, from: Vec2 | null = null, speed: number = C.popSpeed): void {
  const m = w.mode;
  const e = crownEntity(w);
  const h = w.beans[m.crownHolder];
  m.crownHolder = NO_SLOT;
  h.holdsCrown = false;
  if (!e) return;
  let a = Math.hypot(dx, dy) > 1e-9 ? datan2(dy, dx) : h.facing + Math.PI;
  if (spread) a += randRange(w, -C.popSpreadDeg, C.popSpreadDeg) * DEG;
  e.pos.x = from ? from.x : h.pos.x;
  e.pos.y = from ? from.y : h.pos.y;
  e.z = Math.max(h.z, h.groundZ) + C.spawnZ;
  e.vel.x = dcos(a) * speed;
  e.vel.y = dsin(a) * speed;
  e.vz = C.popVz;
  e.data = NO_SLOT;
  e.data2 = h.slot;
  e.timer = C.repickTicks;
  emit(w, EV.CROWN_POP, h.slot, NO_SLOT, 0, 0, h.pos.x, h.pos.y);
}

/** Directions tried by `popToStage`, in steps of 45° alternating around the first choice. */
const STAGE_TRIES = [0, 1, -1, 2, -2, 3, -3, 4] as const;
/** Flight lengths tried by `popToStage`, as fractions of a full pop's. */
const STAGE_REACH = [1, 0.6, 0.3] as const;

/**
 * A pop without a hit to follow (a fall, a grab, a stomp, a peel): the crown hops back toward the
 * middle of the stage (its home), turned in 45° steps and then shortened until it would come down
 * on ground that is still there and quiet once it lands, so it stays in play; with no such spot
 * (a Crumble Cake full of holes) it pops straight up. Deterministic, no RNG.
 */
function popToStage(w: World): void {
  const h = w.beans[w.mode.crownHolder];
  const e = crownEntity(w);
  const map = MAPS[w.cfg.mapId];
  // Start clear of any solid the holder overlaps (a carried bean can hang over a candle).
  const start = popStart;
  start.x = h.pos.x;
  start.y = h.pos.y;
  if (e) {
    e.pos.x = h.pos.x;
    e.pos.y = h.pos.y;
    e.z = Math.max(h.z, h.groundZ) + C.spawnZ;
    e.vel.x = e.vel.y = 0;
    crownVsSolids(w, e);
    start.x = e.pos.x;
    start.y = e.pos.y;
  }
  const home = crownSpawn(w, spawnPoint);
  const a0 = Math.hypot(home.x - start.x, home.y - start.y) > CROWN_TUNING.stageMinDist ? datan2(home.y - start.y, home.x - start.x) : h.facing + Math.PI;
  // Flight time from the pop height down to hovering height over the holder's floor level.
  const g = w.rules.gravity;
  const drop = Math.max(h.z, h.groundZ) - h.groundZ;
  const flight = (C.popVz + Math.sqrt(C.popVz * C.popVz + 2 * g * drop)) / g;
  const ahead = Math.ceil(flight * TICK_HZ) + CROWN_TUNING.landSettleTicks;
  const n = map.solids(w, solidScratch);
  for (const f of STAGE_REACH) {
    const range = C.popSpeed * f * flight;
    for (const k of STAGE_TRIES) {
      const a = a0 + (k * Math.PI) / 4;
      const x = start.x + dcos(a) * range;
      const y = start.y + dsin(a) * range;
      if (stageLanding(w, x, y, n) && map.danger(w, x, y, ahead) < CROWN_TUNING.respawnMaxDanger) {
        popCrown(w, dcos(a), dsin(a), false, start, C.popSpeed * f);
        return;
      }
    }
  }
  popCrown(w, dcos(a0), dsin(a0), false, start, 0);
}

const popStart: Vec2 = v2();

/** A stage pop may come down at (x, y): clear of solids, with floor all around within `landFooting`. */
function stageLanding(w: World, x: number, y: number, n: number): boolean {
  if (!crownSpotClear(w, x, y, n)) return false;
  const map = MAPS[w.cfg.mapId];
  const r = CROWN_TUNING.landFooting;
  for (let i = 0; i < 8; i++) {
    const a = (i * Math.PI) / 4;
    if (map.groundAt(w, x + dcos(a) * r, y + dsin(a) * r) === VOID) return false;
  }
  return true;
}

/**
 * The holder was KO'd still wearing the crown (a KO without a fall: none in Crown Grab today, but
 * `commitKO` allows it): it is knocked off and goes down with them (CROWN_POP, CROWN_LOST, then
 * CROWN_RESPAWN).
 */
function dropWithHolder(w: World): void {
  const m = w.mode;
  const h = w.beans[m.crownHolder];
  m.crownHolder = NO_SLOT;
  h.holdsCrown = false;
  emit(w, EV.CROWN_POP, h.slot, NO_SLOT, 0, 0, h.pos.x, h.pos.y);
  const e = crownEntity(w);
  if (!e) return;
  e.pos.x = h.pos.x;
  e.pos.y = h.pos.y;
  hideCrown(w, e);
}

/** Direction of this tick's latest hit on `victim`, from its HIT event (contact point → center). */
function hitDir(w: World, victim: number, out: Vec2): Vec2 {
  out.x = 0;
  out.y = 0;
  const b = w.beans[victim];
  for (let i = w.events.length - 1; i >= 0; i--) {
    const e = w.events[i];
    if (e.type !== EV.HIT || e.b !== victim || e.tick !== w.tick) continue;
    out.x = b.pos.x - e.x;
    out.y = b.pos.y - e.y;
    break;
  }
  return out;
}

const dir: Vec2 = v2();

/** Pushes a hovering or flying crown out of the solids it can't pass over, with a bounce. */
function crownVsSolids(w: World, e: Entity): void {
  const n = MAPS[w.cfg.mapId].solids(w, solidScratch);
  const r = CROWN_TUNING.radius;
  for (let i = 0; i < n; i++) {
    const s = solidScratch[i];
    if (e.z >= s.top - TUNE.collide.topSlack) continue;
    if (s.kind === SolidKind.CIRCLE) {
      contact.x = s.ax;
      contact.y = s.ay;
    } else {
      closestOnSegment(e.pos.x, e.pos.y, s.ax, s.ay, s.bx, s.by, contact);
    }
    const dx = e.pos.x - contact.x;
    const dy = e.pos.y - contact.y;
    const d = Math.hypot(dx, dy);
    const pen = r + s.r - d;
    if (pen <= 0) continue;
    const nx = d > 1e-9 ? dx / d : 1;
    const ny = d > 1e-9 ? dy / d : 0;
    e.pos.x += nx * pen;
    e.pos.y += ny * pen;
    const px = contact.x + nx * s.r;
    const py = contact.y + ny * s.r;
    const svx = s.vx - s.omega * (py - s.cy);
    const svy = s.vy + s.omega * (px - s.cx);
    const rel = (e.vel.x - svx) * nx + (e.vel.y - svy) * ny;
    if (rel < 0) {
      e.vel.x -= (1 + CROWN_TUNING.restitution) * rel * nx;
      e.vel.y -= (1 + CROWN_TUNING.restitution) * rel * ny;
    }
  }
}

const slide: Vec2 = v2();

/** One tick of a loose crown: flight under gravity, landing to hover, sliding, solids. Both modes. */
function stepLooseCrown(w: World, e: Entity): void {
  const map = MAPS[w.cfg.mapId];
  if (e.timer > 0 && --e.timer === 0) e.data2 = NO_SLOT;
  const g0 = map.groundAt(w, e.pos.x, e.pos.y);
  const rest0 = g0 === VOID ? -Infinity : g0 + C.spawnZ;
  if (e.vz > 0 || e.z > rest0 + 1e-9) {
    const gravity = w.rules.gravity;
    e.z += e.vz * DT - 0.5 * gravity * DT * DT;
    e.vz -= gravity * DT;
  } else {
    e.vz = 0;
    approachVec(e.vel, 0, 0, CROWN_TUNING.friction * DT);
    if (map.slideAccel) {
      map.slideAccel(w, e.pos.x, e.pos.y, slide);
      e.vel.x += slide.x * DT;
      e.vel.y += slide.y * DT;
    }
  }
  e.pos.x += e.vel.x * DT;
  e.pos.y += e.vel.y * DT;
  crownVsSolids(w, e);
  const g = map.groundAt(w, e.pos.x, e.pos.y);
  if (g !== VOID && e.vz <= 0 && e.z <= g + C.spawnZ && e.z >= g - TUNE.bean.airborneZ) {
    e.z = g + C.spawnZ;
    e.vz = 0;
  }
}

/** The crown is gone (over the void, or down with its KO'd holder): it comes back `voidRespawnTicks` later. */
function hideCrown(w: World, e: Entity): void {
  e.z = TUNE.edge.killZ;
  e.vel.x = e.vel.y = 0;
  e.vz = 0;
  e.data = NO_SLOT;
  e.data2 = NO_SLOT;
  e.timer = 0;
  w.mode.crownRespawnTick = w.tick + C.voidRespawnTicks;
  emit(w, EV.CROWN_LOST);
}

function respawnCrown(w: World, e: Entity): void {
  const p = crownRespawnPoint(w, spawnPoint);
  const g = MAPS[w.cfg.mapId].groundAt(w, p.x, p.y);
  if (g === VOID) {
    // Nowhere to land (a Crumble Cake down to a regrowing center): try again shortly.
    w.mode.crownRespawnTick = w.tick + CROWN_TUNING.respawnRetryTicks;
    return;
  }
  e.pos.x = p.x;
  e.pos.y = p.y;
  e.z = g + CROWN_TUNING.respawnDropZ;
  e.vel.x = e.vel.y = 0;
  e.vz = 0;
  w.mode.crownRespawnTick = 0;
  emit(w, EV.CROWN_RESPAWN, NO_SLOT, NO_SLOT, 0, 0, p.x, p.y);
}

/** The closest body that may pick the crown up (ties: lowest slot), or null. */
function picker(w: World, e: Entity): Bean | null {
  let best: Bean | null = null;
  let bestD = Infinity;
  for (const b of w.beans) {
    if (!canPick(b) || (e.data2 === b.slot && e.timer > 0)) continue;
    if (Math.abs(b.z - e.z) > C.pickupMaxDz) continue;
    const d = Math.hypot(b.pos.x - e.pos.x, b.pos.y - e.pos.y);
    if (d <= C.pickupRadius && d < bestD) {
      best = b;
      bestD = d;
    }
  }
  return best;
}

/** Ends the round (server): places by crown time (team time in Teams), ties sharing a place. */
function endRound(w: World): void {
  w.over = true;
  const results = crownResults(w);
  let winner = NO_SLOT;
  let team = 0;
  const firsts = results.filter((r) => r.place === 1);
  if (w.cfg.teamsOn) {
    if (firsts.length > 0 && firsts.every((r) => r.team === firsts[0].team)) {
      winner = TEAM_RESULT;
      team = firsts[0].team;
    }
  } else if (firsts.length === 1) {
    winner = firsts[0].slot;
    team = w.beans[winner].team;
  }
  w.winner = winner;
  for (const r of results) w.beans[r.slot].place = r.place;
  emit(w, EV.ROUND_OVER, winner, team);
}

function crownTick(w: World, clock: RoundClock): void {
  const m = w.mode;
  const e = crownEntity(w);
  if (!e) return;
  if (m.crownHolder !== NO_SLOT) {
    const h = w.beans[m.crownHolder];
    if (popReason(w, h) !== Pop.NONE) {
      popToStage(w);
    } else {
      h.crownTicks++;
      if (crownTimeOf(w, h.slot) >= C.goalTicks) {
        wear(e, h);
        endRound(w);
        return;
      }
    }
  }
  if (m.crownHolder === NO_SLOT) {
    if (m.crownRespawnTick > 0) {
      if (w.tick >= m.crownRespawnTick) respawnCrown(w, e);
    } else {
      stepLooseCrown(w, e);
      const g = MAPS[w.cfg.mapId].groundAt(w, e.pos.x, e.pos.y);
      if (g === VOID && e.z < CROWN_TUNING.lostZ) hideCrown(w, e);
      else {
        const b = picker(w, e);
        if (b) pickUp(w, e, b);
      }
    }
  } else {
    wear(e, w.beans[m.crownHolder]);
  }
  if (clock.tick >= clock.cap) endRound(w);
}

function onTick(w: World, clock: RoundClock, predict: boolean): void {
  tickRespawns(w, predict);
  if (w.tick < 0) return;
  if (predict) {
    // Prediction only flies a loose crown and keeps a worn one on its holder (§18.2).
    const e = crownEntity(w);
    if (!e || w.mode.crownRespawnTick > 0) return;
    if (w.mode.crownHolder !== NO_SLOT) wear(e, w.beans[w.mode.crownHolder]);
    else if (e.z > TUNE.edge.killZ) stepLooseCrown(w, e);
    return;
  }
  if (!w.over) crownTick(w, clock);
}

/** Round results: crown time places (5/3/2/1 in scoring), credited KOs, the team result. */
function crownResults(w: World): RoundResult[] {
  const kos = kosOf(w);
  const active = w.beans.filter((b) => b.active);
  const time = (b: Bean): number => (w.cfg.teamsOn ? crownTimeOf(w, b.slot) : b.crownTicks);
  let winTeam = 0;
  if (w.cfg.teamsOn) {
    let best = -1;
    let tie = false;
    for (const b of active) {
      if (b.team === 0) continue;
      const t = crownTimeOf(w, b.slot);
      if (t > best) {
        best = t;
        winTeam = b.team;
        tie = false;
      } else if (t === best && b.team !== winTeam) {
        tie = true;
      }
    }
    if (tie) winTeam = 0;
  }
  return active.map((b) => ({
    slot: b.slot,
    place: 1 + active.filter((o) => time(o) > time(b)).length,
    kos: kos[b.slot],
    ghostKos: 0,
    bounty: false,
    crownTicks: b.crownTicks,
    survived: !b.out,
    draw: false,
    team: b.team,
    teamWon: winTeam !== 0 && b.team === winTeam,
  }));
}

export const CROWN_GRAB: ModeRules = {
  id: ModeId.CROWN_GRAB,
  name: 'Crown Grab',
  minBeans: 2,
  respawns: true,
  ghosts: false,
  init(w) {
    w.mode.aliveAtStart = countActive(w);
    const p = crownSpawn(w, spawnPoint);
    const g = MAPS[w.cfg.mapId].groundAt(w, p.x, p.y);
    const e = spawnEntity(w, EK.CROWN, NO_SLOT, p.x, p.y, (g === VOID ? 0 : g) + C.spawnZ);
    e.data = NO_SLOT;
    e.data2 = NO_SLOT;
    e.timer = 0;
    w.mode.crownEntId = e.id;
    w.mode.crownHolder = NO_SLOT;
    w.mode.crownRespawnTick = 0;
    koCounts.set(w, new Array<number>(w.beans.length).fill(0));
  },
  onTick,
  onKO(w, victim, credit) {
    if (credit !== NO_SLOT && credit !== victim && !isTeammate(w, credit, victim)) kosOf(w)[credit]++;
    if (w.mode.crownHolder === victim) dropWithHolder(w);
  },
  onHit(w, attacker, victim, cause) {
    if (isPredicting(w) || w.mode.crownHolder !== victim || w.over) return;
    if (!enemyOf(w, attacker, victim) || !crownPopCause(cause)) return;
    // Knocked off along the hit (§4.3); a grab, a peel or a plunger has no HIT to follow.
    hitDir(w, victim, dir);
    if (Math.hypot(dir.x, dir.y) > 1e-9) popCrown(w, dir.x, dir.y);
    else popToStage(w);
  },
  onTouch() {},
  speedMult(w, slot) {
    return slot === w.mode.crownHolder ? C.holderSpeed : 1;
  },
  itemsBlocked(w, slot) {
    return slot === w.mode.crownHolder;
  },
  results: crownResults,
};
